import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import { logoPlatform } from "../../../../../utils/string";
import { useTranslationApp } from "../../../../../lib/i18next";
import {
  PERFORMING_ASSETS_BLUE,
  PERFORMING_ASSETS_CARD_SX,
  PERFORMING_ASSETS_EYEBROW_SX,
  PERFORMING_ASSETS_HAIRLINE,
  PERFORMING_ASSETS_METRIC_VALUE_SX,
  PERFORMING_ASSETS_TITLE_SX,
} from "./performingAssetsStyles";

const METRIC_ROW_QUERY = "@container (min-width: 300px)";

const MetricsRow = ({ statistics }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 1.5,
      mt: 2,
      [METRIC_ROW_QUERY]: {
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      },
    }}
  >
    {statistics.map(({ label, value }) => (
      <Box key={label} sx={{ minWidth: 0 }}>
        <Typography component="p" sx={PERFORMING_ASSETS_EYEBROW_SX}>
          {label}
        </Typography>
        <Typography component="p" noWrap sx={PERFORMING_ASSETS_METRIC_VALUE_SX}>
          {value}
        </Typography>
      </Box>
    ))}
  </Box>
);

const PlatformHeader = ({ platform }) => {
  const { t } = useTranslationApp();
  if (!platform) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Avatar
        data-hide-in-pdf-capture
        src={logoPlatform(platform)}
        variant="square"
        sx={{
          width: 22,
          height: 22,
          ".MuiAvatar-img": { objectFit: "contain" },
        }}
      />
      <Typography
        component="p"
        sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary" }}
      >
        {t(platform)}
      </Typography>
    </Box>
  );
};

const PrimaryValue = ({ subtitle, onSubtitleClick }) => {
  if (!subtitle) return null;

  return (
    <Typography
      component={onSubtitleClick ? "button" : "p"}
      {...(onSubtitleClick ? { type: "button" } : {})}
      onClick={onSubtitleClick}
      sx={{
        mt: 0.75,
        p: 0,
        border: 0,
        bgcolor: "transparent",
        textAlign: "left",
        font: "inherit",
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.25,
        color: PERFORMING_ASSETS_BLUE,
        overflowWrap: "anywhere",
        ...(onSubtitleClick && {
          cursor: "pointer",
          maxWidth: "100%",
          "&:hover": { textDecoration: "underline" },
        }),
      }}
    >
      {subtitle}
    </Typography>
  );
};

const PerformingAssetsCard = ({
  platform,
  title,
  subtitle,
  onSubtitleClick,
  statistics = [],
  nextContent = null,
  media = null,
  layout = "metric",
}) =>
  layout === "split" ? (
    <Box
      data-pdf-card
      sx={{ ...PERFORMING_ASSETS_CARD_SX, containerType: "inline-size" }}
    >
      <PlatformHeader platform={platform} />
      {title ? (
        <Typography component="p" sx={PERFORMING_ASSETS_TITLE_SX}>
          {title}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 2.5,
          "@container (min-width: 640px)": {
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 4,
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <PrimaryValue subtitle={subtitle} onSubtitleClick={onSubtitleClick} />
          {statistics.length > 0 ? (
            <MetricsRow statistics={statistics} />
          ) : null}
        </Box>
        {nextContent ? (
          <Box
            sx={{
              minWidth: 0,
              pt: 2,
              borderTop: `1px solid ${PERFORMING_ASSETS_HAIRLINE}`,
              "@container (min-width: 640px)": {
                pt: 0,
                borderTop: 0,
                pl: 4,
                borderLeft: `1px solid ${PERFORMING_ASSETS_HAIRLINE}`,
              },
            }}
          >
            {nextContent}
          </Box>
        ) : null}
      </Box>
    </Box>
  ) : (
    <Box
      data-pdf-card
      sx={{ ...PERFORMING_ASSETS_CARD_SX, containerType: "inline-size" }}
    >
      <PlatformHeader platform={platform} />

      {title ? (
        <Typography component="p" sx={PERFORMING_ASSETS_TITLE_SX}>
          {title}
        </Typography>
      ) : null}

      {layout === "creative" ? (
        <>
          <PrimaryValue subtitle={subtitle} onSubtitleClick={onSubtitleClick} />
          {media ? <Box sx={{ mt: 1.5 }}>{media}</Box> : null}
          {statistics.length > 0 ? (
            <MetricsRow statistics={statistics} />
          ) : null}
        </>
      ) : (
        <>
          <PrimaryValue subtitle={subtitle} onSubtitleClick={onSubtitleClick} />
          {statistics.length > 0 ? (
            <MetricsRow statistics={statistics} />
          ) : null}
          {media ? <Box sx={{ mt: 2 }}>{media}</Box> : null}
        </>
      )}

      {nextContent ? (
        <Box sx={{ mt: 2, minWidth: 0 }}>{nextContent}</Box>
      ) : null}
    </Box>
  );

export default PerformingAssetsCard;
